import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { CASINO_RULES, SLOT_PAYTABLE, SLOT_REELS, SLOT_STOPS, SLOT_SYMBOL_LABELS, slotSymbolsAt, type SlotSymbol } from "@townsy/contracts";
import { SLOT_MACHINE_NAMES, chipsText } from "./casinoView";
import { offTitle, type Ctx } from "./hudParts";

/**
 * The slot machine view (founder request 2026-10-13: "the slot machine should open up larger for the human user so you
 * see the reels spin and bet that way"). Three big reels that spin and stop one after the other on the SERVER's reel
 * stops (sent when the reels start): the strips only animate to where the server said; there is no client-side result.
 * Bet 1 / 5 / 10, a big SPIN button (Space or Enter while the view has focus), the win line and the paytable.
 */
/** Strip copies drawn per reel, the copy the reels rest on, and the copy a spin starts from (strips move down). */
const COPIES = 5;
const REST_COPY = 1;
const START_COPY = 3;
/** When each reel stops, as a share of the spin (left to right). */
export const REEL_STOP_SHARE = [0.55, 0.775, 1] as const;

/** Index of the TOP visible cell for a reel resting on `stop` (the payline is the middle of three rows). */
export const restRow = (stop: number) => REST_COPY * SLOT_STOPS + stop - 1;
export const startRow = (stop: number) => START_COPY * SLOT_STOPS + stop - 1;

export function SlotGlyph({ symbol }: { symbol: SlotSymbol }) {
  switch (symbol) {
    case "seven":
      return <span className="g-seven">7</span>;
    case "bar":
      return <span className="g-bar">BAR</span>;
    case "cherry":
      return (
        <svg className="g-svg" viewBox="0 0 40 40" aria-hidden="true">
          <path d="M13 26 Q15 12 26 7 M28 27 Q27 15 26 7" stroke="#2f7d32" strokeWidth="2.5" fill="none" />
          <ellipse cx="30" cy="8" rx="7" ry="3" fill="#4caf50" transform="rotate(-20 30 8)" />
          <circle cx="12" cy="29" r="7.5" fill="#d7192a" />
          <circle cx="28" cy="30" r="7.5" fill="#d7192a" />
          <circle cx="9.5" cy="26.5" r="2" fill="#ff8a8a" />
          <circle cx="25.5" cy="27.5" r="2" fill="#ff8a8a" />
        </svg>
      );
    case "bell":
      return (
        <svg className="g-svg" viewBox="0 0 40 40" aria-hidden="true">
          <path d="M7 30 Q10 26 11 18 Q12 6 20 6 Q28 6 29 18 Q30 26 33 30 Z" fill="#e9b21b" stroke="#8a6200" strokeWidth="1.5" />
          <circle cx="20" cy="33" r="3.5" fill="#e9b21b" stroke="#8a6200" strokeWidth="1.5" />
        </svg>
      );
    case "blank":
      return <span className="g-blank" />;
  }
}

export function SlotView({ ctx, machine, onMinimize }: { ctx: Ctx; machine: number; onMinimize(): void }) {
  const [bet, setBet] = useState<1 | 5 | 10>(1);
  const section = useRef<HTMLElement>(null);
  /** Where each machine's reels last came to rest (a new spin starts from there), and the timing of the spin shown. */
  const rest = useRef<Record<number, readonly number[]>>({});
  const timing = useRef<{ machine: number; spin: number; durMs: number; from: readonly number[] } | null>(null);
  const m = ctx.state.slots[machine];

  useEffect(() => {
    section.current?.focus({ preventScroll: true });
  }, [machine]);
  useEffect(() => {
    if (m && !m.spinning) rest.current[machine] = m.stops;
  }, [machine, m, m?.spin, m?.spinning]);

  if (!m) return null;
  const mineHeld = ctx.mine.machine === machine;
  const busy = m.actorId !== null && !mineHeld;
  if (m.spinning && (timing.current?.machine !== machine || timing.current.spin !== m.spin)) {
    timing.current = { machine, spin: m.spin, durMs: Math.max(300, m.endsIn ?? CASINO_RULES.slots.spinMs), from: rest.current[machine] ?? m.stops };
  }
  const anim = m.spinning && timing.current?.machine === machine && timing.current.spin === m.spin ? timing.current : null;
  const canSpin = ctx.betting && !busy && !m.spinning && ctx.chips >= bet;
  const spin = () => {
    if (canSpin) ctx.send({ type: "slot.spin", machine, bet });
  };
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if ((e.key === " " || e.key === "Enter") && e.target === e.currentTarget) {
      e.preventDefault();
      spin();
    }
  };
  const symbols = slotSymbolsAt(m.stops);
  let line: string;
  if (m.spin === 0) line = "Pick your bet and press SPIN.";
  else if (m.spinning) line = "Spinning…";
  else if (!mineHeld) line = `Last spin: ${symbols.map((s) => SLOT_SYMBOL_LABELS[s]).join(" | ")}`;
  else if (m.win > m.bet) line = `${symbols.map((s) => SLOT_SYMBOL_LABELS[s]).join(" | ")}: You won ${chipsText(m.win - m.bet)}!`;
  else if (m.win > 0) line = `${symbols.map((s) => SLOT_SYMBOL_LABELS[s]).join(" | ")}: your ${chipsText(m.bet)} come back.`;
  else line = `${symbols.map((s) => SLOT_SYMBOL_LABELS[s]).join(" | ")}: No win.`;
  const won = !m.spinning && m.win > 0 && m.spin > 0;
  return (
    <section
      ref={section}
      className="casino-big casino-slot-view"
      aria-label={`Slot machine ${machine + 1}`}
      tabIndex={0}
      onKeyDown={onKey}
      data-testid="casino-slot-view"
      data-machine={machine}
    >
      <div className="casino-big-head">
        <h3>
          Slot machine {machine + 1} · {SLOT_MACHINE_NAMES[machine] ?? ""}
        </h3>
        <span className="casino-phase">{busy ? `${m.label ?? "Someone"} is playing` : mineHeld ? "You are playing" : "Free: spin to play"}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onMinimize} data-testid="casino-minimize">
          Minimize
        </button>
      </div>
      <div className="casino-cabinet">
        <div className={`casino-reelbox${won ? " won" : ""}`} role="img" aria-label={m.spinning ? "The reels are spinning" : `Reels: ${symbols.map((s) => SLOT_SYMBOL_LABELS[s]).join(", ")}`}>
          {SLOT_REELS.map((reel, r) => {
            const to = restRow(m.stops[r] ?? 0);
            const from = anim ? startRow(anim.from[r] ?? 0) : to;
            const style = { "--from": from, "--to": to, "--dur": `${Math.round((anim?.durMs ?? 0) * REEL_STOP_SHARE[r]!)}ms` } as CSSProperties;
            return (
              <div className="casino-reel" key={r} aria-hidden="true">
                <div key={`${machine}:${m.spin}`} className={`casino-strip${anim ? " spinning" : ""}`} style={style} data-testid={`casino-reel-${r}`} data-stop={m.stops[r]}>
                  {Array.from({ length: COPIES }, (_, c) =>
                    reel.map((s, i) => (
                      <div className="casino-cell" key={`${c}:${i}`}>
                        <SlotGlyph symbol={s} />
                      </div>
                    )),
                  )}
                </div>
              </div>
            );
          })}
          <div className="casino-payline" aria-hidden="true" />
        </div>
        <p className={`casino-slot-line${won && mineHeld ? " win" : ""}`} aria-live="polite" data-testid="casino-slot-line">
          {line}
        </p>
      </div>
      <div className="casino-big-controls casino-slot-controls">
        <span className="casino-picker" role="group" aria-label="Bet per spin (play chips)">
          <span className="casino-picker-label">Bet</span>
          {CASINO_RULES.slots.bets.map((v) => (
            <button key={v} type="button" className="btn casino-chip-btn" aria-pressed={bet === v} disabled={!ctx.betting || v > ctx.chips} onClick={() => setBet(v)}>
              {v}
            </button>
          ))}
        </span>
        <button
          type="button"
          className="btn casino-spin-big"
          disabled={!canSpin}
          title={offTitle(ctx.betting, busy ? "Someone else is playing this machine." : m.spinning ? "The reels are still turning." : ctx.chips < bet ? `You have ${chipsText(ctx.chips)}.` : "Space or Enter spins too")}
          onClick={spin}
          data-testid="casino-spin"
        >
          SPIN · {bet}
        </button>
      </div>
      <div className="casino-paytable">
        <span className="casino-picker-label">Pays (times your bet):</span>
        <ul aria-label="Paytable, times your bet">
          {SLOT_PAYTABLE.map((p) => (
            <li key={p.label}>
              {p.label} <b>×{p.pays}</b>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
